import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import {
  createCloudTalkContact,
  deleteCloudTalkContact,
  fetchCloudTalkContacts,
  syncCloudTalkContactsDirectory,
  updateCloudTalkContact,
  type CloudTalkContactInput,
} from '../../lib/demCloudTalkContacts';
import type { DemCloudTalkContact } from '../../types/dem';

type CloudTalkContactsModalProps = {
  open: boolean;
  onClose: () => void;
};

type Draft = {
  name: string;
  company: string;
  title: string;
  website: string;
  industry: string;
  address: string;
  city: string;
  zip: string;
  state: string;
  countryId: string;
  phonesText: string;
  emailsText: string;
  tagsText: string;
};

const EMPTY_DRAFT: Draft = {
  name: '',
  company: '',
  title: '',
  website: '',
  industry: '',
  address: '',
  city: '',
  zip: '',
  state: '',
  countryId: '',
  phonesText: '',
  emailsText: '',
  tagsText: '',
};

function linesToList(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function contactToDraft(contact: DemCloudTalkContact): Draft {
  return {
    name: contact.name,
    company: contact.company,
    title: contact.title,
    website: contact.website,
    industry: contact.industry,
    address: contact.address,
    city: contact.city,
    zip: contact.zip,
    state: contact.state,
    countryId: contact.countryId != null ? String(contact.countryId) : '',
    phonesText: contact.phones.join('\n'),
    emailsText: contact.emails.join('\n'),
    tagsText: contact.tags.join(', '),
  };
}

function draftToInput(draft: Draft): CloudTalkContactInput {
  const countryRaw = draft.countryId.trim();
  const countryId = countryRaw ? Number(countryRaw) : null;
  return {
    name: draft.name,
    company: draft.company,
    title: draft.title,
    website: draft.website,
    industry: draft.industry,
    address: draft.address,
    city: draft.city,
    zip: draft.zip,
    state: draft.state,
    countryId: countryId != null && Number.isFinite(countryId) ? countryId : null,
    phones: linesToList(draft.phonesText),
    emails: linesToList(draft.emailsText),
    tags: linesToList(draft.tagsText),
  };
}

export function CloudTalkContactsModal({ open, onClose }: CloudTalkContactsModalProps) {
  const [contacts, setContacts] = useState<DemCloudTalkContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<DemCloudTalkContact | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  const load = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const rows = await fetchCloudTalkContacts();
      setContacts(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load contacts.');
      setContacts([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setEditing(null);
      setCreating(false);
      setDraft(EMPTY_DRAFT);
      setQuery('');
      setError(null);
      setStatus(null);
      return;
    }
    void load(true);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((row) => {
      const hay = [
        row.name,
        row.company,
        row.title,
        row.phones.join(' '),
        row.emails.join(' '),
        row.tags.join(' '),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, query]);

  const formOpen = creating || !!editing;

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setError(null);
    setStatus(null);
  };

  const openEdit = (contact: DemCloudTalkContact) => {
    setCreating(false);
    setEditing(contact);
    setDraft(contactToDraft(contact));
    setError(null);
    setStatus(null);
  };

  const closeForm = () => {
    setCreating(false);
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setStatus(null);
    try {
      const synced = await syncCloudTalkContactsDirectory();
      await load(false);
      setStatus(`Synced ${synced} contact${synced === 1 ? '' : 's'} from CloudTalk.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Contacts sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    const input = draftToInput(draft);
    if (!input.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      if (editing) {
        await updateCloudTalkContact(editing.contactId, input, editing);
        setStatus('Contact updated in CloudTalk and Portal.');
      } else {
        await createCloudTalkContact(input);
        setStatus('Contact created in CloudTalk and Portal.');
      }
      closeForm();
      await load(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save contact.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contact: DemCloudTalkContact) => {
    if (!window.confirm(`Delete CloudTalk contact “${contact.name || contact.contactId}”?`)) {
      return;
    }
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      await deleteCloudTalkContact(contact.contactId);
      if (editing?.contactId === contact.contactId) closeForm();
      setStatus('Contact deleted in CloudTalk and Portal.');
      await load(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="CloudTalk contacts"
      panelClassName="modal-panel--cloudtalk-contacts"
      headerActions={
        <div className="dem-cloudtalk-contacts-actions">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={syncing || loading || saving}
            onClick={() => void handleSync()}
          >
            {syncing ? 'Syncing…' : 'Sync from CloudTalk'}
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={saving}
            onClick={openCreate}
          >
            Add contact
          </button>
        </div>
      }
    >
      <div className="dem-cloudtalk-contacts">
        {status ? (
          <p className="dem-uid-status dem-uid-status--ok" role="status">
            {status}
          </p>
        ) : null}
        {error ? <p className="alert alert-error">{error}</p> : null}

        <div className="dem-cloudtalk-contacts__toolbar">
          <input
            type="search"
            className="input"
            placeholder="Search name, phone, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="dem-cloudtalk-dem-muted">
            {loading ? 'Loading…' : `${filtered.length} contact${filtered.length === 1 ? '' : 's'}`}
          </span>
        </div>

        <div className="dem-cloudtalk-contacts__layout">
          <div className="dem-cloudtalk-contacts__list">
            {loading && contacts.length === 0 ? (
              <p className="dem-cloudtalk-empty">Loading contacts…</p>
            ) : filtered.length === 0 ? (
              <p className="dem-cloudtalk-empty">
                No contacts yet. Sync from CloudTalk or add one here.
              </p>
            ) : (
              filtered.map((contact) => (
                <button
                  key={contact.contactId}
                  type="button"
                  className={`dem-cloudtalk-contact-row${
                    editing?.contactId === contact.contactId
                      ? ' dem-cloudtalk-contact-row--selected'
                      : ''
                  }`}
                  onClick={() => openEdit(contact)}
                >
                  <strong>{contact.name || 'Unnamed'}</strong>
                  <span>{contact.company || '—'}</span>
                  <span className="dem-cloudtalk-dem-muted">
                    {contact.phones[0] || contact.emails[0] || `ID ${contact.contactId}`}
                  </span>
                </button>
              ))
            )}
          </div>

          {formOpen ? (
            <div className="dem-cloudtalk-contacts__form">
              <div className="dem-cloudtalk-detail__ai-head">
                <h3 className="dem-cloudtalk-detail__ai-title">
                  {editing ? 'Edit contact' : 'New contact'}
                </h3>
                <button type="button" className="btn-secondary btn-sm" onClick={closeForm}>
                  Cancel
                </button>
              </div>

              <label className="field">
                <span className="field-label">Name *</span>
                <input
                  className="input"
                  value={draft.name}
                  onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">Company</span>
                <input
                  className="input"
                  value={draft.company}
                  onChange={(e) => setDraft((prev) => ({ ...prev, company: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">Title</span>
                <input
                  className="input"
                  value={draft.title}
                  onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">Phones (one per line)</span>
                <textarea
                  className="input"
                  rows={3}
                  value={draft.phonesText}
                  onChange={(e) => setDraft((prev) => ({ ...prev, phonesText: e.target.value }))}
                  placeholder="+6591234567"
                />
              </label>
              <label className="field">
                <span className="field-label">Emails (one per line)</span>
                <textarea
                  className="input"
                  rows={2}
                  value={draft.emailsText}
                  onChange={(e) => setDraft((prev) => ({ ...prev, emailsText: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">Tags (comma separated)</span>
                <input
                  className="input"
                  value={draft.tagsText}
                  onChange={(e) => setDraft((prev) => ({ ...prev, tagsText: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">Website</span>
                <input
                  className="input"
                  value={draft.website}
                  onChange={(e) => setDraft((prev) => ({ ...prev, website: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">Industry</span>
                <input
                  className="input"
                  value={draft.industry}
                  onChange={(e) => setDraft((prev) => ({ ...prev, industry: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">Address</span>
                <input
                  className="input"
                  value={draft.address}
                  onChange={(e) => setDraft((prev) => ({ ...prev, address: e.target.value }))}
                />
              </label>
              <div className="dem-cloudtalk-contacts__grid">
                <label className="field">
                  <span className="field-label">City</span>
                  <input
                    className="input"
                    value={draft.city}
                    onChange={(e) => setDraft((prev) => ({ ...prev, city: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">ZIP</span>
                  <input
                    className="input"
                    value={draft.zip}
                    onChange={(e) => setDraft((prev) => ({ ...prev, zip: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">State</span>
                  <input
                    className="input"
                    value={draft.state}
                    onChange={(e) => setDraft((prev) => ({ ...prev, state: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Country ID</span>
                  <input
                    className="input"
                    value={draft.countryId}
                    onChange={(e) => setDraft((prev) => ({ ...prev, countryId: e.target.value }))}
                    placeholder="CloudTalk country id"
                  />
                </label>
              </div>

              <div className="dem-cloudtalk-contacts__form-foot">
                {editing ? (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={saving}
                    onClick={() => void handleDelete(editing)}
                  >
                    Delete
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  disabled={saving}
                  onClick={() => void handleSave()}
                >
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Create in CloudTalk'}
                </button>
              </div>
            </div>
          ) : (
            <p className="dem-cloudtalk-empty dem-cloudtalk-contacts__hint">
              Select a contact to edit, or add a new one. Changes go to CloudTalk first, then Portal.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
